import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { preferencesAPI, EffectivePreferences, PreferencesChoices, UserPreferences, coreAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { useView } from './ViewContext';

// Exchange rates type
interface ExchangeRates {
  base_currency: string;
  rates: Record<string, string>;
  as_of: string;
}

// Platform base currency (all backend values are in this currency)
const PLATFORM_BASE_CURRENCY = 'GBP';

// Platform defaults
const PLATFORM_DEFAULTS: EffectivePreferences = {
  currency: 'GBP',
  currency_display: 'British Pound (GBP)',
  timezone: 'UTC',
  timezone_display: 'UTC',
  date_format: 'DD/MM/YYYY',
  date_format_display: 'DD/MM/YYYY (UK Format)',
  language: 'en-GB',
  language_display: 'English (UK)',
  decimal_separator: '.',
  thousands_separator: ',',
  default_site_id: null,
  default_site_name: null,
  sources: {
    currency: 'platform',
    timezone: 'platform',
    date_format: 'platform',
    language: 'platform',
    decimal_separator: 'platform',
    thousands_separator: 'platform',
  },
};

interface PreferencesContextType {
  // The effective (computed) preferences with cascade applied
  preferences: EffectivePreferences;
  // Raw user preferences (for editing)
  userPreferences: UserPreferences | null;
  // Available choices for dropdowns
  choices: PreferencesChoices | null;
  // Exchange rates for currency conversion
  exchangeRates: ExchangeRates | null;
  // Loading states
  isLoading: boolean;
  isUpdating: boolean;
  // Error state
  error: string | null;
  // Actions
  loadPreferences: () => Promise<void>;
  updateUserPreferences: (data: Partial<UserPreferences>) => Promise<void>;
  // Helper functions for formatting
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  /**
   * Convert a value from base currency (GBP) to user's preferred currency and format it.
   * Use this for backend values that are already normalized to GBP.
   * @param valueInBaseCurrency - The value in GBP (base currency)
   * @param targetCurrency - Optional target currency (defaults to user's preference)
   */
  formatCurrencyFromBase: (valueInBaseCurrency: number, targetCurrency?: string) => string;
  /**
   * Convert a value from base currency (GBP) to target currency without formatting.
   * @param valueInBaseCurrency - The value in GBP
   * @param targetCurrency - Optional target currency (defaults to user's preference)
   * @returns The converted numeric value
   */
  convertFromBase: (valueInBaseCurrency: number, targetCurrency?: string) => number;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  formatDateTime: (date: string | Date) => string;
  formatNumber: (value: number, showDecimals?: boolean) => string;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

/**
 * Provides user/site/company-level preferences with a cascade hierarchy.
 * Handles currency conversion from the platform base currency (GBP), date/number formatting,
 * and reloads preferences automatically when the view context changes.
 */
export const PreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewLoading } = useView();

  const [preferences, setPreferences] = useState<EffectivePreferences>(PLATFORM_DEFAULTS);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track previous view context to detect changes
  const prevViewRef = useRef<{ site: number | null; company: number | null; group: number | null }>({
    site: null,
    company: null,
    group: null,
  });

  // Load view-based effective preferences based on current view context
  const loadViewPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(PLATFORM_DEFAULTS);
      return;
    }

    setError(null);

    try {
      // Determine which context to use based on view level
      const params: { site?: number | null; company?: number | null; group?: number | null } = {};

      switch (viewLevel) {
        case 'SITE':
          if (selectedSiteId) params.site = selectedSiteId;
          break;
        case 'COMPANY':
          if (selectedCompanyId) params.company = selectedCompanyId;
          break;
        case 'GROUP':
          if (selectedGroupId) params.group = selectedGroupId;
          break;
      }

      const effectiveRes = await preferencesAPI.getViewEffectivePreferences(params);
      setPreferences(effectiveRes.data);
    } catch (err) {
      console.error('Failed to load view preferences:', err);
      // Fall back to user preferences on error
      try {
        const effectiveRes = await preferencesAPI.getEffectivePreferences();
        setPreferences(effectiveRes.data);
      } catch {
        setPreferences(PLATFORM_DEFAULTS);
      }
    }
  }, [isAuthenticated, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Load exchange rates
  const loadExchangeRates = useCallback(async () => {
    try {
      const response = await coreAPI.getLatestExchangeRates();
      setExchangeRates(response.data);
    } catch (err) {
      console.error('Failed to load exchange rates:', err);
      // Set fallback rates if API fails
      setExchangeRates({
        base_currency: PLATFORM_BASE_CURRENCY,
        rates: {
          GBP: '1.000000',
          USD: '1.27',
          EUR: '1.17',
          JPY: '190.00',
          CNY: '9.20',
          INR: '106.00',
          AUD: '1.95',
          CAD: '1.73',
          CHF: '1.12',
          MXN: '21.70',
        },
        as_of: new Date().toISOString().split('T')[0],
      });
    }
  }, []);

  // Load initial data (choices and user preferences for reference)
  const loadPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(PLATFORM_DEFAULTS);
      setUserPreferences(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Load choices, user preferences, and exchange rates
      const [userRes, choicesRes] = await Promise.all([
        preferencesAPI.getUserPreferences(),
        preferencesAPI.getPreferencesChoices(),
      ]);

      setUserPreferences(userRes.data);
      setChoices(choicesRes.data);

      // Load exchange rates in parallel
      await Promise.all([
        loadViewPreferences(),
        loadExchangeRates(),
      ]);
    } catch (err) {
      console.error('Failed to load preferences:', err);
      setError('Failed to load preferences');
      setPreferences(PLATFORM_DEFAULTS);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, loadViewPreferences, loadExchangeRates]);

  const updateUserPreferences = useCallback(async (data: Partial<UserPreferences>) => {
    setIsUpdating(true);
    setError(null);

    try {
      const response = await preferencesAPI.updateUserPreferences(data);
      setUserPreferences(response.data);

      // Reload view-based effective preferences
      await loadViewPreferences();
    } catch (err) {
      console.error('Failed to update preferences:', err);
      setError('Failed to save preferences');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [loadViewPreferences]);

  // Load preferences when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      loadPreferences();
    } else {
      setPreferences(PLATFORM_DEFAULTS);
      setUserPreferences(null);
      setChoices(null);
    }
  }, [isAuthenticated, loadPreferences]);

  // Reload preferences when view context changes (but not on initial load)
  useEffect(() => {
    const prevView = prevViewRef.current;
    const viewChanged = (
      prevView.site !== selectedSiteId ||
      prevView.company !== selectedCompanyId ||
      prevView.group !== selectedGroupId
    );

    // Update ref for next comparison
    prevViewRef.current = {
      site: selectedSiteId,
      company: selectedCompanyId,
      group: selectedGroupId,
    };

    // Only reload if view actually changed (not on initial mount) and view is loaded
    if (viewChanged && isAuthenticated && !viewLoading && (prevView.site !== null || prevView.company !== null || prevView.group !== null)) {
      loadViewPreferences();
    }
  }, [selectedSiteId, selectedCompanyId, selectedGroupId, isAuthenticated, viewLoading, loadViewPreferences]);

  // Helper: Format currency value (no conversion - value is assumed to be in the specified currency)
  const formatCurrency = useCallback((value: number, currency?: string, showDecimals: boolean | number = false): string => {
    const currencyCode = currency || preferences.currency;
    const decimals = typeof showDecimals === 'number' ? showDecimals : showDecimals ? 3 : 0;
    // Zero (and nullish/NaN) values render without a currency symbol — "£0.00"
    // is visually noisy on dashboards, lists, and exports. Show plain "0".
    if (!value) {
      return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(0);
    }
    const formatted = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
    // CR-4351: Intl formats USD as "US$" in en-GB locale — normalize to "$"
    return formatted.replace('US$', '$');
  }, [preferences.currency]);

  // Helper: Convert value from base currency (GBP) to target currency
  const convertFromBase = useCallback((valueInBaseCurrency: number, targetCurrency?: string): number => {
    const targetCode = targetCurrency || preferences.currency;

    // If already in base currency or no exchange rates, return as-is
    if (targetCode === PLATFORM_BASE_CURRENCY || !exchangeRates) {
      return valueInBaseCurrency;
    }

    // Get the exchange rate (1 GBP = X target currency)
    const rateStr = exchangeRates.rates[targetCode];
    if (!rateStr) {
      console.warn(`No exchange rate found for ${targetCode}, returning base value`);
      return valueInBaseCurrency;
    }

    const rate = parseFloat(rateStr);
    if (isNaN(rate) || rate === 0) {
      return valueInBaseCurrency;
    }

    // Convert: value in GBP * rate = value in target currency
    return valueInBaseCurrency * rate;
  }, [preferences.currency, exchangeRates]);

  // Helper: Convert from base currency and format
  const formatCurrencyFromBase = useCallback((valueInBaseCurrency: number, targetCurrency?: string): string => {
    const convertedValue = convertFromBase(valueInBaseCurrency, targetCurrency);
    return formatCurrency(convertedValue, targetCurrency);
  }, [convertFromBase, formatCurrency]);

  // Helper: Convert date to user's timezone
  const convertToUserTimezone = useCallback((date: Date): Date => {
    // Use Intl.DateTimeFormat to convert the date to user's timezone
    const timezone = preferences.timezone;
    try {
      // Get the date parts in the target timezone
      const options: Intl.DateTimeFormatOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };
      const formatter = new Intl.DateTimeFormat('en-CA', options);
      const parts = formatter.formatToParts(date);

      const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';

      return new Date(
        parseInt(getPart('year')),
        parseInt(getPart('month')) - 1,
        parseInt(getPart('day')),
        parseInt(getPart('hour')),
        parseInt(getPart('minute')),
        parseInt(getPart('second'))
      );
    } catch {
      // If timezone is invalid, return original date
      return date;
    }
  }, [preferences.timezone]);

  // Helper: Format date according to user preferences (timezone-aware)
  const formatDate = useCallback((date: string | Date, options?: { format?: string; includeTime?: boolean }): string => {
    // Date-only strings ("YYYY-MM-DD") must NOT go through timezone conversion
    // because new Date("2025-12-30") is parsed as UTC midnight, and converting
    // to a local timezone behind UTC shifts the date back by one day.
    const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
    let dateObj: Date;
    if (isDateOnly) {
      const [y, m, d] = (date as string).split('-').map(Number);
      dateObj = new Date(y, m - 1, d); // local time, no TZ shift
    } else {
      const inputDate = typeof date === 'string' ? new Date(date) : date;
      // Convert to user's timezone
      dateObj = convertToUserTimezone(inputDate);
    }
    const dateFormat = options?.format || preferences.date_format;

    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[dateObj.getMonth()];

    let formattedDate: string;
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        formattedDate = `${day}/${month}/${year}`;
        break;
      case 'MM/DD/YYYY':
        formattedDate = `${month}/${day}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formattedDate = `${year}-${month}-${day}`;
        break;
      case 'DD-MMM-YYYY':
        formattedDate = `${day}-${monthShort}-${year}`;
        break;
      default:
        formattedDate = `${day}/${month}/${year}`;
    }

    if (options?.includeTime) {
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      formattedDate += ` ${hours}:${minutes}`;
    }

    return formattedDate;
  }, [preferences.date_format, convertToUserTimezone]);

  // Helper: Format date and time according to user preferences
  const formatDateTime = useCallback((date: string | Date): string => {
    return formatDate(date, { includeTime: true });
  }, [formatDate]);

  // Helper: Format number with thousands separator
  const formatNumber = useCallback((value: number, showDecimals: boolean = false): string => {
    if (showDecimals) {
      const parts = value.toFixed(2).split('.');
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, preferences.thousands_separator);
      return `${integerPart}${preferences.decimal_separator}${parts[1]}`;
    }
    const integerPart = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, preferences.thousands_separator);
    return integerPart;
  }, [preferences.decimal_separator, preferences.thousands_separator]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        userPreferences,
        choices,
        exchangeRates,
        isLoading,
        isUpdating,
        error,
        loadPreferences,
        updateUserPreferences,
        formatCurrency,
        formatCurrencyFromBase,
        convertFromBase,
        formatDate,
        formatDateTime,
        formatNumber,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Accesses the effective user preferences (currency, timezone, date format) and formatting helpers.
 * Must be used within a {@link PreferencesProvider}.
 *
 * @returns Preferences state with formatCurrency, formatDate, formatNumber, and currency conversion helpers
 */
export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

/**
 * Convenience hook that exposes only the formatting functions from PreferencesContext.
 * Use this in components that only need formatCurrency/formatDate/formatNumber
 * without the full preferences editing state.
 */
export const useFormatters = () => {
  const { formatCurrency, formatCurrencyFromBase, convertFromBase, formatDate, formatDateTime, formatNumber, preferences, exchangeRates } = usePreferences();
  return { formatCurrency, formatCurrencyFromBase, convertFromBase, formatDate, formatDateTime, formatNumber, preferences, exchangeRates };
};
